/**
 * BIRHAUS Unified Bulk Action Types v2.2.3
 * 
 * Central type definitions for all bulk operations following BIRHAUS principles:
 * - Spanish-first internationalization
 * - Undo-over-confirm patterns
 * - Cognitive load reduction (max 3 actions per context)
 * - Type safety with proper generics
 */

import type { ReactNode } from 'react'

/**
 * Bulk action variants following BIRHAUS visual hierarchy
 */
export type BulkActionVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive'

/**
 * Bulk action execution states
 */
export type BulkActionState = 'idle' | 'executing' | 'completed' | 'error'

/**
 * Base interface for all bulk actions
 * Contains common properties following Spanish-first principle
 */
export interface BirhausBulkActionBase<T = any> {
  /** Unique identifier for the action - required for tracking and analytics */
  id: string
  
  /** Spanish label (required - BIRHAUS Spanish-first principle) */
  labelEs: string
  
  /** English label (optional fallback) */
  labelEn?: string
  
  /** Icon component for visual recognition */
  icon?: React.ComponentType<{ className?: string }>
  
  /** Button variant following BIRHAUS visual hierarchy */
  variant?: BulkActionVariant
  
  /** Whether the action is disabled */
  disabled?: boolean
  
  /** Minimum number of items required for this action */
  minItems?: number
  
  /** Maximum number of items allowed for this action */
  maxItems?: number
  
  /** Custom validation function */
  validate?: (items: T[]) => { valid: boolean; message?: string }
  
  /** Whether to clear selection after successful completion */
  clearSelectionOnComplete?: boolean
}

/**
 * Simple bulk action without undo capability
 * Use for non-destructive operations like export, view, etc.
 */
export interface BirhausBulkActionSimple<T = any> extends BirhausBulkActionBase<T> {
  /** Action type identifier */
  type: 'simple'
  
  /** Action handler - returns void or Promise<void> */
  onClick: (selectedItems: T[]) => void | Promise<void>
}

/**
 * Undoable bulk action following BIRHAUS undo-over-confirm principle
 * Use for destructive operations like delete, archive, etc.
 */
export interface BirhausBulkActionUndoable<T = any> extends BirhausBulkActionBase<T> {
  /** Action type identifier */
  type: 'undoable'
  
  /** Action handler - must return undo function */
  onClick: (selectedItems: T[]) => Promise<{ undoAction: () => void | Promise<void> }>
  
  /** How long the undo option stays available (ms) - default 10000 */
  undoTimeout?: number
  
  /** Whether this action supports undo (always true for undoable actions) */
  undoable: true
}

/**
 * Unified bulk action type - discriminated union for type safety
 */
export type BirhausBulkAction<T = any> = BirhausBulkActionSimple<T> | BirhausBulkActionUndoable<T>

/**
 * Undo action information for tracking and display
 */
export interface UndoActionInfo {
  /** Unique identifier for the undo action */
  id: string
  
  /** Spanish label for undo toast */
  labelEs: string
  
  /** English label for undo toast */
  labelEn?: string
  
  /** The undo function to execute */
  action: () => void | Promise<void>
  
  /** How long this undo option is available (ms) */
  timeout: number
  
  /** Timestamp when undo was created */
  createdAt: Date
}

/**
 * Progress information for bulk operations
 */
export interface ProgressInfo {
  /** Current progress count */
  current: number
  
  /** Total items to process */
  total: number
  
  /** Optional message describing current operation */
  message?: string
  
  /** Optional message in Spanish */
  messageEs?: string
  
  /** Optional message in English */
  messageEn?: string
  
  /** Whether the operation can be cancelled */
  cancellable?: boolean
}

/**
 * Result of a bulk action execution
 */
export interface BulkActionResult<T = any> {
  /** Whether the action was successful */
  success: boolean
  
  /** Items that were processed */
  processedItems: T[]
  
  /** Items that failed to process */
  failedItems?: T[]
  
  /** Error message if action failed */
  error?: string
  
  /** Spanish error message */
  errorEs?: string
  
  /** English error message */
  errorEn?: string
  
  /** Undo action info if available */
  undoInfo?: UndoActionInfo
}

/**
 * Type guards for discriminated union
 */
export function isSimpleAction<T>(action: BirhausBulkAction<T>): action is BirhausBulkActionSimple<T> {
  return action.type === 'simple'
}

export function isUndoableAction<T>(action: BirhausBulkAction<T>): action is BirhausBulkActionUndoable<T> {
  return action.type === 'undoable'
}

/**
 * Action builder for simple actions
 */
export function createSimpleAction<T = any>(
  config: Omit<BirhausBulkActionSimple<T>, 'type'>
): BirhausBulkActionSimple<T> {
  return {
    type: 'simple',
    ...config
  }
}

/**
 * Action builder for undoable actions
 */
export function createUndoableAction<T = any>(
  config: Omit<BirhausBulkActionUndoable<T>, 'type' | 'undoable'>
): BirhausBulkActionUndoable<T> {
  return {
    type: 'undoable',
    undoable: true,
    undoTimeout: 10000, // 10 second default
    ...config
  }
}

/**
 * Validation utilities
 */
export namespace BulkActionValidation {
  /**
   * Validates that actions follow BIRHAUS cognitive load principles
   * Maximum 3 bulk actions per context (4-3-1 rule)
   */
  export function validateCognitiveLoad<T>(actions: BirhausBulkAction<T>[]): {
    valid: boolean
    message?: string
    messageEs?: string
    messageEn?: string
  } {
    if (actions.length > 3) {
      return {
        valid: false,
        message: `Too many bulk actions: ${actions.length}. BIRHAUS 4-3-1 rule allows maximum 3 contextual actions.`,
        messageEs: `Demasiadas acciones masivas: ${actions.length}. La regla BIRHAUS 4-3-1 permite máximo 3 acciones contextuales.`,
        messageEn: `Too many bulk actions: ${actions.length}. BIRHAUS 4-3-1 rule allows maximum 3 contextual actions.`
      }
    }
    
    return { valid: true }
  }
  
  /**
   * Validates Spanish-first labeling
   */
  export function validateSpanishFirst<T>(action: BirhausBulkAction<T>): {
    valid: boolean
    message?: string
  } {
    if (!action.labelEs) {
      return {
        valid: false,
        message: `Action "${action.id}" missing required labelEs (Spanish-first principle)`
      }
    }
    
    return { valid: true }
  }
  
  /**
   * Validates destructive actions have undo capability
   */
  export function validateDestructiveActions<T>(action: BirhausBulkAction<T>): {
    valid: boolean
    message?: string
  } {
    const destructiveIds = ['delete', 'remove', 'archive', 'destroy', 'purge']
    const isDestructive = destructiveIds.some(id => action.id.toLowerCase().includes(id)) || 
                          action.variant === 'destructive'
    
    if (isDestructive && !isUndoableAction(action)) {
      return {
        valid: false,
        message: `Destructive action "${action.id}" should be undoable (BIRHAUS undo-over-confirm principle)`
      }
    }
    
    return { valid: true }
  }
}

/**
 * Legacy compatibility types - deprecated in v2.2.3
 * @deprecated Use BirhausBulkAction instead
 */
export interface LegacyBulkAction<T = any> {
  labelEs: string
  labelEn?: string
  icon?: React.ComponentType<{ className?: string }>
  onClick: (selectedItems: T[]) => void | Promise<void>
  variant?: BulkActionVariant
  requireConfirm?: boolean
  disabled?: boolean
}

/**
 * Migration helper to convert legacy actions to new format
 */
export function migrateLegacyAction<T = any>(
  legacy: LegacyBulkAction<T>,
  id: string
): BirhausBulkAction<T> {
  // If requireConfirm was used, assume it should be undoable instead
  if (legacy.requireConfirm) {
    console.warn(`Legacy action with requireConfirm detected. Converting to undoable action. Consider updating to use undo pattern instead of confirmation.`)
    
    return createUndoableAction<T>({
      id,
      labelEs: legacy.labelEs,
      labelEn: legacy.labelEn,
      icon: legacy.icon,
      variant: legacy.variant,
      disabled: legacy.disabled,
      onClick: async (items) => ({
        undoAction: async () => {
          console.warn('Legacy undo action - implement proper undo logic')
        }
      })
    })
  }
  
  return createSimpleAction<T>({
    id,
    labelEs: legacy.labelEs,
    labelEn: legacy.labelEn,
    icon: legacy.icon,
    variant: legacy.variant,
    disabled: legacy.disabled,
    onClick: legacy.onClick
  })
}